import URI from 'urijs';
import { EntityType, StationSubType } from '../../../Constants';
import Container from '../../../Container';
import Chapter from '../../../dataServices/Chapter';
import Episode from '../../../dataServices/Episode';
import StandaloneChapter from '../../../dataServices/StandaloneChapter';
import Station from '../../../dataServices/Station';
import { type TPlayableObject } from '../../types';
import  { type IRewindStationMachineContext } from '../RewindStationMachine'
import { addTimes, isContentTypeAd, sFromTime, subtractTimes, timeFromMs, timeFromS } from './utils';

const serviceBus = Container;

export type StreamType = 'BROADCAST' | 'STANDALONE_CHAPTER';
export type PlayableStreamObject =
  | {
      streamType: 'BROADCAST';
      station: Station;
      episode?: Episode;
      standaloneChapter: undefined;
    }
  | {
      streamType: 'STANDALONE_CHAPTER';
      station: undefined;
      episode: undefined;
      standaloneChapter: StandaloneChapter;
    };
export type TLoadParams = {
  url: string;
  isPodcast: boolean;
  isSuperHifi: boolean;
  seekingInChapter: boolean,
}

export const findCurrentChapter = (
  chapters: Array<Chapter>| undefined,
  episode: Episode | undefined,
  time: number,
): number | undefined => {
  if (!chapters || !episode) {
    return;
  }

  const currentWallClockTime = addTimes(timeFromMs(episode.getStartTimeMillis()), timeFromS(time));

  for (let i = chapters.length - 1; i >= 0; i--) {
    const chapter = chapters[i];
    if (chapter.getStartOffset() <= sFromTime(currentWallClockTime)) {
      return i;
    }
  }
};

export const getCurrentChapterIndex = ({
  chapters,
  episode,
  elapsed,
  currentChapterIndex: oldCurrentChapterIndex,
  logger,
  metadata,
}: IRewindStationMachineContext) => {
  let newIndex: number | undefined;

  if (metadata?.isLive && chapters) {
    newIndex = chapters.length - 1;
  } else {
    newIndex = findCurrentChapter(chapters, episode, elapsed ?? 0);
  }

  if (newIndex !== oldCurrentChapterIndex) {
    logger?.info(`Current chapter index changed from ${oldCurrentChapterIndex} to ${newIndex}`);
  }

  return {
    currentChapterIndex: newIndex,
  };
}

export const  getLiveChapter = (episode: Episode, chapters: Array<Chapter>) => {
  let startOffset: number;
  if (chapters.length > 0) {
    const lastElement = chapters[chapters.length - 1];
    startOffset = lastElement.data.startOffset + lastElement.data.duration + 10; // NOTICE: added 10 seconds for the difference between chapters for correct switching
  } else {
    startOffset = episode.getStartTimeSeconds();
  }

  return new Chapter({ startOffset, title: `Part ${chapters.length + 1}` });
}

export const getLoadParams = (ctx: IRewindStationMachineContext): TLoadParams => {
  const { logger, ddLogger, requestedOffset, standaloneChapter, source, seekingInChapter } = ctx;

  if (!source) {
    ddLogger?.error('No playable streamUrl found (SSR)');
    throw new Error('No playable streamUrl found (SSR)');
  }

  const url = new URI(source.url);
  const isStandaloneChapter = ctx.streamType === 'STANDALONE_CHAPTER';

  let isInChapter = seekingInChapter;

  // NOTE:  No requestOffset signifies a non-live rewind stream
  if (requestedOffset !== undefined && !ctx.playLive) {
    url.setQuery('startTs', requestedOffset);

    if (isStandaloneChapter) {
      const endTs = standaloneChapter?.getEndTimeSeconds();
      url.addQuery('endTs', endTs);
    }

    // NOTE: [CCS-2636] - VOD - only add mode=vod if the episode is rewindable and we are playing a VOD stream/not live
    if (isVod(ctx)) {
      url.addQuery('mode', 'vod');
      // adds a check to see if the user is on the live chapter, but not the live stream
      // preventing the stream from requesting an ad while just behind live
      if (!isLive(ctx) && isLiveChapter(ctx)) {
        url.addQuery('inChapter', true)
      } else {
        url.addQuery('inChapter', seekingInChapter ?? true);
      }
      isInChapter = true;

    } else if (ctx.retryCount && ctx.retryCount > 0) {
      // We can use this when trying to reconnect so a user doesn't immediately get an ad when they reconnect
      url.addQuery('inChapter', true);
      isInChapter = true;
    } else {
      url.addQuery('inChapter', seekingInChapter !== false);
      isInChapter = seekingInChapter !== false;
    }

    logger?.debug(
      `Requesting SSR stream with wall clock offset ${requestedOffset}, ${
        seekingInChapter ? 'within the current chapter' : 'across a chapter boundary'
      }`,
    );
  }

  url.addQuery('newMeta', 'true');

  logger?.info(`Requesting SSR stream with URL ${url.valueOf()}`);
  ddLogger?.info(`Requesting SSR stream with URL ${url.valueOf()}`);

  return {
    url: url.valueOf(),
    isPodcast: source.type !== 'm3u8',
    isSuperHifi: false,
    seekingInChapter: isInChapter ?? false,
  };
}

/**
 * Convert a dataObject into a strongly typed object that we can use in RewindStationMachine
 */
export const getPlayableDataObject = async (
  dataObject: TPlayableObject,
): Promise<PlayableStreamObject | undefined> => {
  let station = dataObject instanceof Station ? dataObject : undefined;
  const episode = dataObject instanceof Episode ? dataObject : undefined;
  const standaloneChapter = dataObject instanceof StandaloneChapter ? dataObject : undefined;

  // Standalone chapter
  if (standaloneChapter) {
    return {
      streamType: 'STANDALONE_CHAPTER',
      station: undefined,
      episode: undefined,
      standaloneChapter,
    };
  }

  // Get parent station if playing AmperWave radio broadcast by episode
  const stationId = episode?.getParentStation()?.getId();
  if (stationId) {
    station = (await serviceBus.dataServices.getContentObject(stationId)) as Station;
  }

  // Play AmperWave radio broadcast
  if (
    station &&
    station.getEntityType() === EntityType.STATION &&
    station.getEntitySubtype() === StationSubType.BROADCAST &&
    station.isRewindable()
  ) {
    return {
      streamType: 'BROADCAST',
      station,
      episode,
      standaloneChapter: undefined,
    };
  }

  // No playable object
  return;
};

const isLive = (ctx: IRewindStationMachineContext) => {
  return ctx.metadata?.isLive ?? false;
};

const isLiveChapter = (ctx: IRewindStationMachineContext) => {
  const lastChapter = ctx.chapters?.[ctx.chapters.length - 1];
  const endOfLastChapter = (lastChapter?.data.startOffset ?? 0) + (lastChapter?.data.duration ?? 0);
  const isRequestOffsetGreaterThanEndOfLastChapter = (ctx.requestedOffset ?? 0) > endOfLastChapter;

  return isRequestOffsetGreaterThanEndOfLastChapter;
};

const isVod = (ctx: IRewindStationMachineContext) => {
  return !!ctx.player.vodFeatureEnabled && ctx.episode?.isRewindable() && !ctx.playLive;
};

export const mapPlayerTime = (ctx: IRewindStationMachineContext, time: number) => {
  const { metadata, episode, standaloneChapter } = ctx;

  const entity = standaloneChapter ?? episode;

  const entityStartTime = timeFromMs(entity?.getStartTimeMillis() ?? 0);

  // ATL is StreamGuys which has no datumTime, so use entityStartTime
  // datumTime is essentially the startTs / resume point when playing a rewind stream relative to unix epoch in seconds
  // datumTime is usually equal to startTs so that's the best way to think of it
  // time is the elapsed time since we started playing the stream
  // so datumTime + time = the current playback timestamp relative to unix epoch
  const datumTime = metadata?.datumTime ? timeFromS(metadata.datumTime) : entityStartTime;

  const elapsed = sFromTime(addTimes(subtractTimes(datumTime, entityStartTime), timeFromS(time)));

  return { elapsed };
};

/**
 * Normalizes absolute (epoch) and relative (episode start) resume points for use
 * as elapsed player time in seconds. Converts as follows:
 * Broadcast - to absolute elapsed times, because we use AmperWave
 */
export const normalizeResumePoint = ({
  resumePoint,
  entity,
  streamType,
}: {
  resumePoint?: number;
  entity: Episode | StandaloneChapter | undefined;
  streamType: StreamType;
}): number | undefined => {
  // Resume points not allowed for non-rewindable episodes
  if (!entity?.isRewindable()) {
    return undefined;
  }

  const now = Date.now() / 1000;
  const year2000 = new Date('2000').getTime() / 1000;
  const entityStart = entity.getStartTimeSeconds();
  const entityEnd = entity.getEndTimeSeconds();

  // Reset corrupted resume points
  if (!resumePoint || resumePoint < 0 || resumePoint >= entityEnd || resumePoint > now) {
    // If the resume point is corrupted and the requested entity is LIVE, play LIVE
    if (streamType === 'BROADCAST' && entityEnd > now) {
      return undefined;
    }
    resumePoint = entityStart;
  }

  // TODO: The chapter selection from the UI doesn't like decimals too much
  // We could just send in the non-truncated version of this decimal, but the UI will stutter a bit
  // So we are doing this to get to the nearest higher millisecond + 1
  // to make sure we don't overlap with the possible ending of another chapter
  const resumePointRoundedUp = Math.ceil(resumePoint) + 1;
  const absoluteResumePoint =
    resumePointRoundedUp >= year2000 ? resumePointRoundedUp : entityStart + resumePointRoundedUp;

  if (streamType === 'BROADCAST') {
    return absoluteResumePoint;
  }

  if (streamType === 'STANDALONE_CHAPTER') {
    return absoluteResumePoint;
  }
};

export function performSeek(ctx: IRewindStationMachineContext, time: number): IRewindStationMachineContext {
  const { metadata, episode, standaloneChapter, chapters, currentChapterIndex } = ctx;
  const entity = standaloneChapter ?? episode;
  // StreamGuys uses 'seconds since episode start'
  const requestedOffset = normalizeResumePoint({
    resumePoint: time,
    entity,
    streamType: ctx.streamType,
  });

  const episodeSeekingInChapter =
    currentChapterIndex !== undefined &&
    findCurrentChapter(chapters, episode, time) === currentChapterIndex;

  return {
      ...ctx,
      seekingInChapter: isVod(ctx) ? true : episodeSeekingInChapter,
      requestedOffset,
      elapsed: time,
      metadata: {
        ...metadata,
        // ATL is StreamGuys, so keep datumTime undefined
        datumTime: metadata?.datumTime ? requestedOffset : undefined,
      },
    };
}

export function saveResumePoint(ctx: IRewindStationMachineContext, resumePoint: number) : IRewindStationMachineContext {
  return {
      ...ctx,
      requestedOffset:
          ctx.player.vodFeatureEnabled && isContentTypeAd(ctx.metadata?.contentType)
          ? ctx.requestedOffset
          : resumePoint,
  };
}

export function saveResumePoints(ctx: IRewindStationMachineContext, shouldSaveOnServer = true) {
  const { elapsed, personalizationProvider, episode, standaloneChapter, item } =
      ctx;
  const entity = episode ?? standaloneChapter;
  if (!entity || elapsed === undefined) {
      return ctx;
  }

  ctx.timeSinceLastSavedResumePoint = Date.now();

  const playbackOffset = entity.getStartTimeSeconds() + elapsed;

  const entityPlayback = { contentId: entity.getId(), playbackOffset};
  const itemPlayback = item.data.id ? { contentId: item.data.id as string, playbackOffset } : undefined;
  const playbacks  = itemPlayback ? [ entityPlayback, itemPlayback  ] : [ entityPlayback ];


  void personalizationProvider.setPlaybacks(playbacks, shouldSaveOnServer );

  return saveResumePoint(ctx, playbackOffset);
}
